/**
 * Columns for the Disciplines tab's two record lists — the person's
 * disciplines and skills. Read-only, plain text, sortable. Dates are plain
 * `date` columns (calendar days), rendered with `safeFormatDate`.
 */
import type { PersonDisciplineWire, PersonSkillWire } from "@releve/shared";
import type { ColumnConfig } from "@/components/ui/editable-table";
import { NumberDisplay } from "@/components/ui/number-display";
import { safeFormatDate } from "@/lib/dateUtils";
import { dash, flagLabel, textCell, type TranslateFn } from "./cells";

export interface PersonDisciplineGridRow extends Record<string, unknown> {
  id: string;
  name: string;
  is_primary: boolean;
  start_date: string;
  end_date: string;
}

export interface PersonSkillGridRow extends Record<string, unknown> {
  id: string;
  name: string;
  category: string;
  proficiency_level: string;
  years_experience: number | null;
  last_used: string;
}

/** Primary first, then by name. */
export function toPersonDisciplineGridRows(disciplines: PersonDisciplineWire[]): PersonDisciplineGridRow[] {
  return disciplines
    .map((row) => ({
      id: row.id,
      name: row.discipline?.name ?? "",
      is_primary: Boolean(row.is_primary),
      start_date: row.start_date ?? "",
      end_date: row.end_date ?? "",
    }))
    .sort((a, b) => Number(b.is_primary) - Number(a.is_primary) || a.name.localeCompare(b.name));
}

export function toPersonSkillGridRows(skills: PersonSkillWire[]): PersonSkillGridRow[] {
  return skills
    .map((row) => ({
      id: row.id,
      name: row.skill?.name ?? "",
      category: row.skill?.category ?? "",
      proficiency_level: row.proficiency_level ?? "",
      years_experience: row.years_experience,
      last_used: row.last_used ?? "",
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** A skill category token → its label (free text passes through). */
export function skillCategoryLabel(t: TranslateFn, category: unknown): string {
  const token = String(category ?? "");
  return token ? t(`people:form.skillCategories.${token}`, { defaultValue: token }) : "";
}

/** A proficiency token → its label. */
export function proficiencyLabel(t: TranslateFn, level: unknown): string {
  const token = String(level ?? "");
  return token ? t(`common:people.proficiency.${token}`, { defaultValue: token }) : "";
}

const dateCell = (value: unknown) => textCell(safeFormatDate(value as string | null, "PP", ""));

export function buildPersonDisciplineGridColumns(t: TranslateFn): ColumnConfig[] {
  const header = (key: string) => t(`people:tabs.disciplines.disciplines.columns.${key}`);
  return [
    {
      data: "name",
      type: "text",
      header: header("name"),
      width: 240,
      sortable: true,
      readOnly: true,
      cellRenderer: (value) => textCell(value),
    },
    {
      data: "is_primary",
      type: "text",
      header: header("primary"),
      width: 96,
      sortable: true,
      readOnly: true,
      displayValue: (value) => flagLabel(t, value),
      cellRenderer: (value) => textCell(flagLabel(t, value)),
    },
    {
      data: "start_date",
      type: "text",
      header: header("startDate"),
      width: 140,
      sortable: true,
      readOnly: true,
      cellRenderer: dateCell,
    },
    {
      data: "end_date",
      type: "text",
      header: header("endDate"),
      width: 140,
      sortable: true,
      readOnly: true,
      cellRenderer: dateCell,
    },
  ];
}

export function buildPersonSkillGridColumns(t: TranslateFn): ColumnConfig[] {
  const header = (key: string) => t(`people:tabs.disciplines.skills.columns.${key}`);
  return [
    {
      data: "name",
      type: "text",
      header: header("name"),
      width: 220,
      sortable: true,
      readOnly: true,
      cellRenderer: (value) => textCell(value),
    },
    {
      data: "category",
      type: "text",
      header: header("category"),
      width: 180,
      sortable: true,
      readOnly: true,
      displayValue: (value) => skillCategoryLabel(t, value),
      cellRenderer: (value) => textCell(skillCategoryLabel(t, value)),
    },
    {
      data: "proficiency_level",
      type: "text",
      header: header("proficiency"),
      width: 140,
      sortable: true,
      readOnly: true,
      displayValue: (value) => proficiencyLabel(t, value),
      cellRenderer: (value) => textCell(proficiencyLabel(t, value)),
    },
    {
      data: "years_experience",
      type: "numeric",
      header: header("years"),
      width: 110,
      sortable: true,
      readOnly: true,
      cellRenderer: (value) =>
        value == null || value === "" ? (
          dash
        ) : (
          <span className="font-mono text-xs">
            <NumberDisplay value={Number(value)} decimals={0} />
          </span>
        ),
    },
    {
      data: "last_used",
      type: "text",
      header: header("lastUsed"),
      width: 140,
      sortable: true,
      readOnly: true,
      cellRenderer: dateCell,
    },
  ];
}
